import type { FC } from 'react';

import {
  type ResolveParams,
  useMatchRoute,
  useRouter,
} from '@tanstack/react-router';

import { type TabItemProps, Tabs, type TabsProps } from '@iconik-io/uad';

import type { RoutePath } from '../../router/types';

export type UadRouteTab = {
  [TRoute in RoutePath]: Omit<TabItemProps, 'href'> & {
    route: TRoute;
  } & (keyof ResolveParams<TRoute> extends never
      ? { params?: never }
      : { params: ResolveParams<TRoute> });
}[RoutePath];

type Props = Omit<TabsProps, 'items' | 'selectedKey' | 'onSelectionChange'> & {
  tabs: UadRouteTab[];
  hideIfNotMatched?: boolean;
};

export const UadRouteTabs: FC<Props> = ({
  tabs,
  hideIfNotMatched = false,
  ...tabsProps
}) => {
  const matchRoute = useMatchRoute();
  const router = useRouter();

  if (tabs.length === 0) {
    return null;
  }

  const selectedTab = tabs.find((tab) =>
    matchRoute({ to: tab.route, params: tab.params }),
  );

  if (!selectedTab && hideIfNotMatched) {
    return null;
  }

  const items = tabs.map((tab) => ({
    ...tab,
    href: router.buildLocation({ to: tab.route, params: tab.params }).href,
  }));

  // Falling back to the first tab keeps the underlying react-aria Tabs
  // controlled even when no tab route matches (e.g. on a parent route
  // before an index redirect).
  return (
    <Tabs
      {...tabsProps}
      items={items}
      selectedKey={selectedTab?.id ?? tabs[0]?.id}
    />
  );
};
